import { createSlice } from '@reduxjs/toolkit';

import { Duration } from 'models/auction/Duration';
import { Shipping, ShippingWay } from 'models/auction/Shipping';
import { Auction, ProductCondition } from 'models/auction/Auction';

import { hoursToSeconds } from 'utils/date';
import { Image } from 'models/auction/Image';

type Metadata = 'id' | 'errors' | 'currentStep' | 'syncedAuction';
export type AuctionFormField = Exclude<keyof AuctionFormState, Metadata>;

interface AuctionFormState {
  // metadata (each key should be match with 'Metadata' type)
  id: string;
  errors: AuctionFormField[];
  syncedAuction: Auction | null; // last version of auction in server database

  // data
  title: string;
  condition: ProductCondition;
  description: string;
  duration: Duration;
  images: Image[];
  shippingMethods: Shipping[];
  payment: string;
  returnPolicy: string;
  invitationCodeRequired: boolean;
}

const initialState: AuctionFormState = {
  // metadata
  id: '',
  errors: [],
  syncedAuction: null,

  // step 0: 경매 정보 입력
  title: '',
  condition: 'used',
  description: '',
  duration: hoursToSeconds(24),
  images: [],

  // step 1: 거래 정보 입력
  shippingMethods: [],
  payment: '',
  returnPolicy: '',
  invitationCodeRequired: false,
};

const auctionFormSlice = createSlice({
  name: 'auctionForm',
  initialState,
  reducers: {
    /**
     * Initialize auction form state.
     */
    initAuctionFormState: (state, action: { payload: Auction }) => {
      const {
        id,
        title,
        condition,
        description,
        duration,
        images,
        shipping_methods,
        payment,
        return_policy,
        invitation_code_required,
      } = action.payload;

      const newState = { ...state };

      if (id) newState.id = id;
      if (title) newState.title = title;
      if (condition) newState.condition = condition;
      if (description) newState.description = description;
      if (duration) newState.duration = duration;
      if (images?.length) newState.images = [...images];
      if (shipping_methods?.length) newState.shippingMethods = [...shipping_methods];
      if (payment) newState.payment = payment;
      if (return_policy) newState.returnPolicy = return_policy;
      if (invitation_code_required) newState.invitationCodeRequired = invitation_code_required;

      newState.syncedAuction = action.payload;

      return newState;
    },

    resetAuctionFormState: () => ({ ...initialState }),

    /**
     * General slice for update auction form state.
     * If specific slice exists for target field, must use specific one.
     */
    setAuctionFormState: (state, action: { payload: Partial<AuctionFormState> }) => {
      const newErrors = state.errors.filter((error) => !Object.keys(action.payload).includes(error)); // clear error of updated fields
      return { ...state, ...action.payload, errors: newErrors };
    },

    /**
     * Specific slice for add image.
     * new image with Image type should be in payload.
     * This slice assume given image is not null, and not duplicated.
     */
    addImage: (state, action: { payload: Image }) => {
      state.images.push(action.payload);
      state.errors = state.errors.filter((error) => error !== 'images');
    },

    /**
     * Specific slice for remove image.
     * image id to remove should be in payload.
     */
    removeImage: (state, action: { payload: string }) => {
      state.images = state.images.filter(({ id }) => id !== action.payload);
      state.errors = state.errors.filter((error) => error !== 'images');
    },

    /**
     * Specific slice for add shipping method.
     * new shipping method should be in payload.
     * This slice assume given shipping method is not empty, and not duplicated.
     */
    createShippingMethod: (state, action: { payload: Shipping }) => {
      const newShippingMethod = action.payload;

      // cost is nullable in front-end, but not in back-end
      if (newShippingMethod.cost === null) newShippingMethod.cost = 0;

      const errorsExcludeShippingMethods = state.errors.filter((error) => error !== 'shippingMethods');
      return {
        ...state,
        shippingMethods: [...state.shippingMethods, newShippingMethod],
        errors: errorsExcludeShippingMethods,
      };
    },

    /**
     * Specific slice for update shipping method.
     * new shipping method should be in payload.
     * This slice assume given shipping method is not empty, and not duplicated.
     * also assume shipping type does not changed.
     */
    updateShippingMethod: (state, action: { payload: { keyDescription: string; newShippingMethod: Shipping } }) => {
      const { keyDescription, newShippingMethod } = action.payload;

      // cost is nullable in front-end, but not in back-end
      if (newShippingMethod.cost === null) newShippingMethod.cost = 0;

      // assume shipping type does not changed
      const type = newShippingMethod.type;

      const errorsExcludeShippingMethods = state.errors.filter((error) => error !== 'shippingMethods');
      const newShippingMethods = state.shippingMethods.map((shippingMethod) =>
        shippingMethod.type === type && shippingMethod.description === keyDescription
          ? newShippingMethod
          : shippingMethod
      );

      return { ...state, shippingMethods: newShippingMethods, errors: errorsExcludeShippingMethods };
    },

    /**
     * Specific slice for remove shipping method.
     * shipping method has unique set of (type and description)
     * if target (type and description) does not exist, ignore request.
     */
    removeShippingMethod: (state, action: { payload: { type: ShippingWay; keyDescription: string } }) => {
      const { type, keyDescription } = action.payload;

      const errorsExcludeShippingMethods = state.errors.filter((error) => error !== 'shippingMethods');
      const newShippingMethods = state.shippingMethods.filter(
        (method) => method.type !== type || method.description !== keyDescription
      );

      return { ...state, shippingMethods: newShippingMethods, errors: errorsExcludeShippingMethods };
    },

    clearShippingMethodError: (state) => {
      const errorsExcludeShippingMethods = state.errors.filter((error) => error !== 'shippingMethods');
      return { ...state, errors: errorsExcludeShippingMethods };
    },

    /**
     * Specific slice for set errors.
     */
    setErrors: (state, action: { payload: AuctionFormField[] }) => {
      state.errors = action.payload;
    },

    updateSyncedAuction: (state, action: { payload: Auction }) => ({
      ...state,
      syncedAuction: action.payload,
    }),
  },
});

export const getAuctionFormErrorMsg = (field: AuctionFormField) => {
  switch (field) {
    case 'images':
      return '최소 하나의 이미지를 업로드해주세요';
    case 'shippingMethods':
      return '직거래 장소 또는 배송 정보 중 최소 하나를 입력해주세요';
    default:
      return '값을 입력해주세요';
  }
};

export const {
  initAuctionFormState,
  resetAuctionFormState,
  setAuctionFormState,
  addImage,
  removeImage,
  createShippingMethod,
  updateShippingMethod,
  removeShippingMethod,
  clearShippingMethodError,
  setErrors,
  updateSyncedAuction,
} = auctionFormSlice.actions;

export default auctionFormSlice.reducer;
